import {
  SIM_REPORT_REASONS,
  type SimMetrics,
  type SimReportGroup,
  type SimShareCard,
  type SimSwitchesView,
} from '@cruz/shared';
import { useEffect, useId, useMemo, useState, type ReactNode } from 'react';
import { SimApiError, simAdminApi, simErrorMessage } from './api.ts';
import {
  card,
  inputClass,
  Notice,
  PageTitle,
  primaryButton,
  secondaryButton,
  Working,
} from './ui.tsx';

/**
 * /simulador/admin: the review panel. Reports to accept or dismiss, the switches of
 * the AI and the launch metrics. The key lives in this tab only (sessionStorage).
 */

const KEY_STORAGE = 'cruz-sim-admin-key';

function storedKey(): string {
  try {
    return sessionStorage.getItem(KEY_STORAGE) ?? '';
  } catch {
    return '';
  }
}
function storeKey(key: string | null) {
  try {
    if (key) sessionStorage.setItem(KEY_STORAGE, key);
    else sessionStorage.removeItem(KEY_STORAGE);
  } catch {
    // Private mode: the key lasts until reload
  }
}

type Api = ReturnType<typeof simAdminApi>;
type Section = 'reports' | 'switches' | 'metrics';

const adminError = (err: unknown) =>
  err instanceof SimApiError && err.status === 401
    ? 'Esa clave no es la del panel.'
    : simErrorMessage(err);

export function AdminPage() {
  // A key from earlier in this tab goes straight in; a wrong one shows in each section
  const [key, setKey] = useState(storedKey);
  const api = useMemo(() => simAdminApi(key), [key]);

  if (!key) {
    return (
      <AdminLogin
        onKey={async (k) => {
          await simAdminApi(k).switches();
          storeKey(k);
          setKey(k);
        }}
      />
    );
  }
  return (
    <AdminPanel
      api={api}
      onLogout={() => {
        storeKey(null);
        setKey('');
      }}
    />
  );
}

function AdminLogin({ onKey }: { onKey: (k: string) => Promise<void> }) {
  const id = useId();
  const [value, setValue] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const submit = async (k: string) => {
    setBusy(true);
    setError(null);
    try {
      await onKey(k.trim());
    } catch (err) {
      setError(adminError(err));
      setBusy(false);
    }
  };

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        void submit(value);
      }}
    >
      <PageTitle kicker="De cirio a hermano mayor">Panel de revisión</PageTitle>
      <div>
        <label htmlFor={id} className="mb-1.5 block text-base">
          Clave del panel
        </label>
        <input
          id={id}
          type="password"
          autoComplete="current-password"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          className={inputClass}
        />
      </div>
      {error && <Notice tone="error">{error}</Notice>}
      <button type="submit" className={primaryButton} disabled={busy || value.trim().length === 0}>
        {busy ? 'Comprobando…' : 'Entrar al panel'}
      </button>
    </form>
  );
}

function AdminPanel({ api, onLogout }: { api: Api; onLogout: () => void }) {
  const [section, setSection] = useState<Section>('reports');
  const sections: [Section, string][] = [
    ['reports', 'Reportes'],
    ['switches', 'Interruptores'],
    ['metrics', 'Métricas'],
  ];
  return (
    <div className="space-y-5">
      <div className="flex items-start justify-between gap-3">
        <PageTitle kicker="De cirio a hermano mayor">Panel de revisión</PageTitle>
        <button
          type="button"
          className="rounded-full px-3 py-2 text-sm font-medium hover:bg-cream-200 dark:hover:bg-night-700"
          onClick={onLogout}
        >
          Salir
        </button>
      </div>
      <div role="tablist" aria-label="Secciones" className="flex gap-2">
        {sections.map(([s, label]) => (
          <button
            key={s}
            type="button"
            role="tab"
            aria-selected={section === s}
            onClick={() => setSection(s)}
            className={`min-h-10 rounded-full px-4 py-1.5 text-sm font-semibold ${
              section === s
                ? 'bg-purple text-cream dark:bg-gold-300 dark:text-night'
                : 'border border-purple/25 text-purple dark:border-cream/20 dark:text-cream'
            }`}
          >
            {label}
          </button>
        ))}
      </div>
      <div role="tabpanel">
        {section === 'reports' && <ReportsSection api={api} />}
        {section === 'switches' && <SwitchesSection api={api} />}
        {section === 'metrics' && <MetricsSection api={api} />}
      </div>
    </div>
  );
}

// ------------------------------------------------------------------ reports

interface EventSnapshot {
  title: string;
  text: string;
  options: { text: string; outcome: string }[];
  source: string;
}

const dateTime = (iso: string) =>
  new Date(iso).toLocaleString('es-ES', {
    timeZone: 'Europe/Madrid',
    day: 'numeric',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
  });

function ReportsSection({ api }: { api: Api }) {
  const [groups, setGroups] = useState<SimReportGroup[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  useEffect(() => {
    api
      .reports()
      .then((r) => setGroups(r.groups))
      .catch((err: unknown) => setError(adminError(err)));
  }, [api]);

  if (error) return <Notice tone="error">{error}</Notice>;
  if (!groups) return <Working label="Cargando los reportes…" />;
  if (groups.length === 0) return <Notice>No hay reportes pendientes.</Notice>;
  return (
    <ul className="space-y-4">
      {groups.map((g) => (
        <ReportItem
          key={g.key}
          group={g}
          onResolve={async (action) => {
            await api.resolve(g.key, action);
            setGroups((gs) => gs?.filter((x) => x.key !== g.key) ?? null);
          }}
        />
      ))}
    </ul>
  );
}

function ReportItem({
  group: g,
  onResolve,
}: {
  group: SimReportGroup;
  onResolve: (action: 'accept' | 'dismiss') => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const resolve = async (action: 'accept' | 'dismiss') => {
    setBusy(true);
    setError(null);
    try {
      await onResolve(action);
    } catch (err) {
      setError(adminError(err));
      setBusy(false);
    }
  };
  const what =
    g.target === 'share'
      ? 'Tarjeta compartida'
      : g.library_id
        ? 'Evento de la biblioteca'
        : 'Evento';
  return (
    <li className={`${card} space-y-3 px-4 py-4`} aria-label={`${what} reportado`}>
      <div className="flex flex-wrap items-center gap-2 text-sm">
        <span className="font-semibold">{what}</span>
        <span className="text-ink-soft dark:text-cream/70">
          · {g.reports.length} {g.reports.length === 1 ? 'reporte' : 'reportes'} · desde{' '}
          {dateTime(g.first_at)}
        </span>
        {g.removed && (
          <span className="rounded-full bg-burgundy-300/30 px-2.5 py-0.5 text-xs font-semibold text-burgundy dark:bg-burgundy/30 dark:text-burgundy-300">
            {g.target === 'share' ? 'Oculta' : 'Retirado'}
          </span>
        )}
      </div>
      {g.target === 'share' ? (
        <ShareSnapshot card={g.snapshot as SimShareCard} />
      ) : (
        <EventSnapshotView event={g.snapshot as EventSnapshot} />
      )}
      <ul className="space-y-1.5 text-[0.95rem]">
        {g.reports.map((r, i) => (
          <li key={i} className="rounded-lg bg-cream-200/70 px-3 py-2 dark:bg-night-700">
            <strong>
              {SIM_REPORT_REASONS[r.reason as keyof typeof SIM_REPORT_REASONS] ?? r.reason}
            </strong>
            {!r.signed_in && (
              <span className="text-ink-soft dark:text-cream/60"> · sin cuenta</span>
            )}
            {r.comment && <p className="mt-0.5 italic">«{r.comment}»</p>}
          </li>
        ))}
      </ul>
      {error && <Notice tone="error">{error}</Notice>}
      <div className="grid grid-cols-2 gap-2">
        <button
          type="button"
          className={primaryButton}
          disabled={busy}
          onClick={() => void resolve('accept')}
        >
          {g.target === 'share' ? 'Ocultar' : 'Retirar'}
        </button>
        <button
          type="button"
          className={secondaryButton}
          disabled={busy}
          onClick={() => void resolve('dismiss')}
        >
          Descartar
        </button>
      </div>
    </li>
  );
}

function EventSnapshotView({ event }: { event: EventSnapshot }) {
  return (
    <div className="space-y-2">
      <p className="font-serif text-xl font-semibold">{event.title}</p>
      <p className="whitespace-pre-line">{event.text}</p>
      <details>
        <summary className="cursor-pointer text-sm font-medium">
          Opciones y desenlaces ({event.source === 'library' ? 'biblioteca' : event.source})
        </summary>
        <ol className="mt-2 list-decimal space-y-2 pl-5 text-[0.95rem]">
          {event.options.map((o, i) => (
            <li key={i}>
              <p className="font-medium">{o.text}</p>
              <p className="text-ink-soft dark:text-cream/75">{o.outcome}</p>
            </li>
          ))}
        </ol>
      </details>
    </div>
  );
}

function ShareSnapshot({ card: c }: { card: SimShareCard }) {
  return (
    <div className="space-y-1">
      <p className="text-xs font-semibold tracking-[0.14em] uppercase">{c.kicker}</p>
      <p className="font-serif text-xl font-semibold">{c.title}</p>
      <p>{c.phrase}</p>
      <p className="text-sm text-ink-soft dark:text-cream/70">
        {c.nickname} · {c.brotherhood} ·{' '}
        <a className="underline" href={`/simulador/logro/${c.id}`} target="_blank" rel="noreferrer">
          ver la tarjeta
        </a>
      </p>
    </div>
  );
}

// ----------------------------------------------------------------- switches

function SwitchesSection({ api }: { api: Api }) {
  const [current, setCurrent] = useState<SimSwitchesView | null>(null);
  const [draft, setDraft] = useState<SimSwitchesView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api
      .switches()
      .then((s) => {
        setCurrent(s);
        setDraft(s);
      })
      .catch((err: unknown) => setError(adminError(err)));
  }, [api]);

  if (!draft || !current) {
    return error ? <Notice tone="error">{error}</Notice> : <Working label="Cargando…" />;
  }
  const change = (patch: Partial<SimSwitchesView>) => {
    setSaved(false);
    setDraft({ ...draft, ...patch });
  };
  const save = async () => {
    setBusy(true);
    setError(null);
    try {
      const s = await api.setSwitches({
        ai: draft.ai,
        ai_live: draft.ai_live,
        daily_budget_eur: draft.daily_budget_eur,
        report_threshold: draft.report_threshold,
      });
      setCurrent(s);
      setDraft(s);
      setSaved(true);
    } catch (err) {
      setError(adminError(err));
    }
    setBusy(false);
  };
  const dirty =
    draft.ai !== current.ai ||
    draft.ai_live !== current.ai_live ||
    draft.daily_budget_eur !== current.daily_budget_eur ||
    draft.report_threshold !== current.report_threshold;
  const validNumbers =
    Number.isFinite(draft.daily_budget_eur) &&
    draft.daily_budget_eur >= 0 &&
    Number.isInteger(draft.report_threshold) &&
    draft.report_threshold >= 1;

  return (
    <form
      className="space-y-4"
      onSubmit={(e) => {
        e.preventDefault();
        void save();
      }}
    >
      <Toggle
        label="IA activada"
        hint="Apagada: solo eventos de la biblioteca y tranquilos, sin revisión ni diario."
        checked={draft.ai}
        locked={current.locked.ai}
        onChange={(ai) => change({ ai })}
      />
      <Toggle
        label="Eventos escritos al momento"
        hint="Apagado: la IA solo revisa y escribe el diario; los eventos salen de la biblioteca."
        checked={draft.ai_live}
        locked={current.locked.ai_live}
        onChange={(ai_live) => change({ ai_live })}
      />
      <NumberField
        label="Presupuesto diario de IA (euros)"
        hint="Al llegar, se juega solo con la biblioteca hasta el día siguiente. Aviso al 80 %."
        value={draft.daily_budget_eur}
        step={0.5}
        min={0}
        max={1000}
        onChange={(daily_budget_eur) => change({ daily_budget_eur })}
      />
      <NumberField
        label="Reportes para retirar o esconder"
        hint="Jugadores distintos que reportan un evento de la biblioteca o una tarjeta."
        value={draft.report_threshold}
        step={1}
        min={1}
        max={100}
        onChange={(report_threshold) => change({ report_threshold })}
      />
      {error && <Notice tone="error">{error}</Notice>}
      {saved && !dirty && <Notice>Guardado. Se aplica en unos segundos.</Notice>}
      <button type="submit" className={primaryButton} disabled={busy || !dirty || !validNumbers}>
        {busy ? 'Guardando…' : 'Guardar los cambios'}
      </button>
    </form>
  );
}

function Toggle({
  label,
  hint,
  checked,
  locked,
  onChange,
}: {
  label: string;
  hint: string;
  checked: boolean;
  locked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <label className={`${card} flex cursor-pointer items-start gap-3 px-4 py-3`}>
      <input
        type="checkbox"
        checked={checked && !locked}
        disabled={locked}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-1 h-5 w-5 flex-none accent-purple dark:accent-gold"
      />
      <span>
        <span className="block font-semibold">{label}</span>
        <span className="block text-sm text-ink-soft dark:text-cream/70">
          {locked
            ? 'Apagado desde el servidor (variables de entorno): no se puede encender aquí.'
            : hint}
        </span>
      </span>
    </label>
  );
}

function NumberField({
  label,
  hint,
  value,
  step,
  min,
  max,
  onChange,
}: {
  label: string;
  hint: string;
  value: number;
  step: number;
  min: number;
  max: number;
  onChange: (v: number) => void;
}) {
  const id = useId();
  return (
    <div>
      <label htmlFor={id} className="block font-semibold">
        {label}
      </label>
      <p className="mb-1.5 text-sm text-ink-soft dark:text-cream/70">{hint}</p>
      <input
        id={id}
        type="number"
        inputMode="decimal"
        value={Number.isNaN(value) ? '' : value}
        step={step}
        min={min}
        max={max}
        onChange={(e) => onChange(e.target.valueAsNumber)}
        className={inputClass}
      />
    </div>
  );
}

// ------------------------------------------------------------------ metrics

const pct = (n: number) => `${Math.round(n * 1000) / 10} %`;
const eur = (n: number) =>
  n.toLocaleString('es-ES', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 });
const shortDay = (day: string) =>
  new Date(`${day}T12:00:00Z`).toLocaleDateString('es-ES', { day: 'numeric', month: 'short' });

function MetricsSection({ api }: { api: Api }) {
  const [days, setDays] = useState(14);
  // Results by days asked: switching shows "Calculando…" until its own answer comes
  const [result, setResult] = useState<{ days: number; m?: SimMetrics; error?: string } | null>(
    null,
  );
  useEffect(() => {
    let alive = true;
    api
      .metrics(days)
      .then((m) => alive && setResult({ days, m }))
      .catch((err: unknown) => alive && setResult({ days, error: adminError(err) }));
    return () => {
      alive = false;
    };
  }, [api, days]);
  const current = result?.days === days ? result : null;
  const m = current?.m ?? null;
  const error = current?.error ?? null;

  return (
    <div className="space-y-5">
      <div className="flex gap-2">
        {[7, 14, 30].map((n) => (
          <button
            key={n}
            type="button"
            aria-pressed={days === n}
            onClick={() => setDays(n)}
            className={`min-h-10 rounded-full px-4 py-1.5 text-sm font-semibold ${
              days === n
                ? 'bg-purple text-cream dark:bg-gold-300 dark:text-night'
                : 'border border-purple/25 dark:border-cream/20'
            }`}
          >
            {n} días
          </button>
        ))}
      </div>
      {error && <Notice tone="error">{error}</Notice>}
      {!m && !error && <Working label="Calculando…" />}
      {m && <MetricsView m={m} />}
    </div>
  );
}

function Stat({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className={`${card} px-4 py-3`}>
      <p className="text-sm text-ink-soft dark:text-cream/70">{label}</p>
      <p className="tabular font-serif text-2xl font-semibold">{children}</p>
    </div>
  );
}

function MetricsView({ m }: { m: SimMetrics }) {
  const t = m.totals;
  const r = m.retention;
  const retention = (x: SimMetrics['retention']['d1']) =>
    x.eligible > 0 ? `${pct(x.rate)} (${x.retained}/${x.eligible})` : '—';
  return (
    <>
      <section aria-label="Totales" className="grid grid-cols-2 gap-3 sm:grid-cols-3">
        <Stat label="Jugadores">{t.users}</Stat>
        <Stat label="Partidas (terminadas)">
          {t.careers} ({t.finished_careers})
        </Stat>
        <Stat label="Aceptan publicidad">{pct(t.marketing_rate)}</Stat>
        <Stat label="Tarjetas compartidas">{t.shares}</Stat>
        <Stat label="Reportes pendientes">{t.open_reports}</Stat>
        <Stat label="Biblioteca (retirados)">
          {t.library_active} ({t.library_retired})
        </Stat>
        <Stat label="Coste de IA total">{eur(t.ai_cost_eur)}</Stat>
        <Stat label="Vuelven al día 1">{retention(r.d1)}</Stat>
        <Stat label="Vuelven al día 7">{retention(r.d7)}</Stat>
        <Stat label="Vuelven al día 30">{retention(r.d30)}</Stat>
      </section>
      {/* Focusable: the table scrolls sideways on a phone, also with the keyboard */}
      <div
        role="region"
        aria-label="Métricas por día"
        tabIndex={0}
        className="overflow-x-auto rounded-xl border border-purple/15 dark:border-cream/15"
      >
        <table className="tabular w-full min-w-[40rem] text-right text-sm">
          <caption className="sr-only">Por día de juego</caption>
          <thead className="bg-cream-200 dark:bg-night-700">
            <tr>
              {[
                'Día',
                'Altas',
                'Activos',
                'Eventos',
                'Ev./jug.',
                'Coste IA',
                '€/jug.',
                'Revisados',
                'Rechazo',
                'Reportes',
                'Tarjetas',
              ].map((h) => (
                <th key={h} scope="col" className="px-2.5 py-2 font-semibold first:text-left">
                  {h}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...m.days].reverse().map((d) => (
              <tr key={d.day} className="border-t border-purple/10 dark:border-cream/10">
                <th scope="row" className="px-2.5 py-1.5 text-left font-medium whitespace-nowrap">
                  {shortDay(d.day)}
                  {d.day === m.today ? ' (hoy)' : ''}
                </th>
                <td className="px-2.5 py-1.5">{d.signups}</td>
                <td className="px-2.5 py-1.5">{d.active_players}</td>
                <td className="px-2.5 py-1.5">{d.events}</td>
                <td className="px-2.5 py-1.5">{d.events_per_player}</td>
                <td className="px-2.5 py-1.5">{eur(d.ai_cost_eur)}</td>
                <td className="px-2.5 py-1.5">{eur(d.cost_per_player_eur)}</td>
                <td className="px-2.5 py-1.5">{d.reviews}</td>
                <td className="px-2.5 py-1.5">{pct(d.rejection_rate)}</td>
                <td className="px-2.5 py-1.5">{d.reports}</td>
                <td className="px-2.5 py-1.5">{d.shares}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
